import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { Loader2, ArrowLeft, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import InstructorCard from '../dashboard/InstructorCard';

export default function InstructorsCarousel() {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where('role', '==', 'instructor'),
          where('instructorData.status', '==', 'approved')
        );
        const snap = await getDocs(q);
        
        // Firestore requires composite index for where + orderBy if we use registrationDate. 
        // We'll sort in memory to avoid index requirement for now.
        const docs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        docs.sort((a, b) => b.registrationDate?.toMillis() - a.registrationDate?.toMillis());
        
        setInstructors(docs.slice(0, 6)); // Top 6
      } catch (err) {
        console.error('Error fetching instructors:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInstructors();
  }, []);

  if (loading) {
    return (
      <div className="py-20 flex justify-center text-primary-500">
        <Loader2 className="w-10 h-10 animate-spin" />
      </div>
    );
  }

  if (instructors.length === 0) return null;

  return (
    <section className="py-16 sm:py-24 relative overflow-hidden bg-slate-50/50 dark:bg-black/20" dir="rtl">
      {/* Background elements */}
      <div className="absolute top-0 right-1/4 w-[250px] sm:w-[400px] lg:w-[500px] h-[250px] sm:h-[400px] lg:h-[500px] bg-primary-500/5 dark:bg-amber-500/5 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 sm:mb-12 gap-4 sm:gap-6">
          <div>
            <div className="flex items-center gap-2 text-primary-600 dark:text-amber-500 font-bold mb-2 sm:mb-3 text-sm sm:text-base">
              <Users className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>نخبة الخبراء</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-slate-900 dark:text-white">
              تعلم من <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-amber-500">أفضل المُدربين</span>
            </h2>
          </div>
          <Link 
            to="/inspirers" 
            className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-slate-900 dark:text-white border border-slate-200 dark:border-white/10 rounded-2xl font-bold transition-all hover:scale-105 active:scale-95 group shadow-sm"
          >
            <span>استعرض جميع المدربين</span>
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Carousel / Scroll container */}
        <div className="flex overflow-x-auto gap-4 sm:gap-6 pb-6 sm:pb-8 snap-x snap-mandatory hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
          {instructors.map((instructor) => (
            <div key={instructor.id} className="min-w-[260px] sm:min-w-[300px] md:min-w-[350px] w-[75vw] sm:w-[60vw] md:w-[350px] snap-center shrink-0">
              <InstructorCard instructor={instructor} />
            </div>
          ))}
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}} />
    </section>
  );
}
